import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Radio,
  RadioGroup,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ErrorMessage, Form, Formik, FormikProps, FormikValues } from 'formik';
import QueryString from 'qs';
import { useMutation } from 'react-query';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import Select from 'react-select';
import * as Yup from 'yup';

import emailTemplates from 'api/campaigns';
import ButtonPrimitive from 'components/primitives/Button';
import Typography from 'components/primitives/Typography';
import { PRICE_BY_NEGOTIATION, SET_DATE_OF_SALE } from 'constants/property';
import ucFirstMultiple from 'utils/string/ucFirstMultiple';

import Loader from './Loader/loader';
import PreviewModal from './PreviewCampaign/PreviewCampaign.view';
import QuillEditor from './QuillEditor';

const LOC_DEFAULT = [
  {
    region: '',
    district: [
      {
        name: '',
        suburb: [],
      },
    ],
  },
];

const SCHEMA = Yup.object({
  campaign_name: Yup.string().required('Campaign name is required'),
  email_subject: Yup.string().required('Email subject is required'),
  schedule: Yup.string().required('Send type is required'),
  scheduleDate: Yup.date()
    .nullable()
    .when('schedule', {
      is: 'schedule',
      then: Yup.date().required('Schedule date is required'),
    }),

  date_from: Yup.date()
    .nullable() // Allows null values
    .transform((value, originalValue) =>
      originalValue ? new Date(originalValue) : null
    )
    .when('registrationType', (registrationType: any, schema: any) =>
      registrationType
        ? schema.required('Date is required')
        : schema.notRequired()
    ),

  date_to: Yup.date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue ? new Date(originalValue) : null
    )
    .when('registrationType', (registrationType: any, schema: any) =>
      registrationType === 'between'
        ? schema.required('Date is required')
        : schema.notRequired()
    ),
});

const StyledSelect = (props: any) => (
  <Select
    {...props}
    styles={{
      placeholder: (provided: any) => ({
        ...provided,
        color: '#000',
      }),
    }}
  />
);

const StyledErrorMessage = (props: { name: string }) => (
  <ErrorMessage
    name={props.name}
    component="div"
    //@ts-ignore
    style={{ color: 'red' }}
  />
);

const EmailTemplate = (props: RouteComponentProps): JSX.Element => {
  const history = useHistory();
  const toast = useToast();

  const queries = useMemo(
    () =>
      QueryString.parse(new URLSearchParams(props.location.search).toString()),
    [props.location.search]
  );

  const campaignId = queries.id;

  const [campaignData, setCampaignData] = useState<Record<string, any>>();
  const [isAllSelected, setIsAllSelected] = useState(true);
  const [options, setOptions] = useState<{
    userType: any[];
    propertyTypes: any[];
    location: any[];
    buyerSearchStatus: any[];
    sellerSearchStatus: any[];
    buyerStatus: any[];
    buyerMarketPrice: any[];
    sellerMarketPrice: any[];
    facilitatorStatus: any[];
  }>({
    userType: [],
    propertyTypes: [],
    location: [],
    buyerSearchStatus: [],
    sellerSearchStatus: [],
    buyerStatus: [],
    buyerMarketPrice: [],
    sellerMarketPrice: [],
    facilitatorStatus: [],
  });
  const [selectedLocation, setSelectedLocation] = useState(LOC_DEFAULT);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const { mutate, isLoading } = useMutation(
    (values: FormikValues) => {
      const {
        user_type,
        scheduleDate,
        date_from,
        date_to,
        buyerStatus,
        sellerStatus,
        finance_status,
        property_type,
        market_price,
        broker_status,
        ...rest
      } = values;

      const toValueArray = (arr: any[]) => arr.map((a) => a.value);

      const data: any = {
        ...rest,
        id: campaignId,
        user_type: user_type.value,
        campaign_time: new Date(scheduleDate).getTime(),
        dateFrom: new Date(date_from).getTime(),
        dateTo: new Date(date_to).getTime(),
        location: selectedLocation[0].region === '' ? [] : selectedLocation,
        finance_status: toValueArray(finance_status),
        property_type: toValueArray(property_type),
        market_price: toValueArray(market_price),
        property_status: toValueArray(
          user_type.value === 'sell' ? sellerStatus : buyerStatus
        ),
        broker_status: toValueArray(broker_status),
        save_template: isSavingTemplate,
      };
      return emailTemplates.createOrUpdate(data);
    },
    {
      onSuccess: (response: any) => {
        toast({
          title: 'Success',
          description:
            response?.message || 'Email template saved successfully.',
          status: 'success',
          position: 'top-right',
        });
        setIsSavingTemplate(false);
        history.goBack();
      },
      onError: (error: any) => {
        toast({
          title: 'Error',
          description: error?.message || 'Something went wrong.',
          status: 'error',
          position: 'top-right',
        });
        setIsSavingTemplate(false);
      },
    }
  );

  useEffect(() => {
    // Fetch dropdown options when the page loads
    const fetchDropdownOptions = async () => {
      try {
        const response: any = await emailTemplates.getAllOptions();
        setOptions({
          ...response.data,
          sellerMarketPrice: response.data.sellerMarketPrice.filter(
            (mp: { value: string }) =>
              mp.value !== PRICE_BY_NEGOTIATION && mp.value !== SET_DATE_OF_SALE
          ),
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load dropdown options',
          status: 'error',
        });
      }
    };

    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (campaignId) {
      const fetchDetails = async () => {
        try {
          const response: any = await emailTemplates.getDetails(
            campaignId as string
          );

          setCampaignData(response.data);
          setSelectedLocation(
            response.data.location && response.data.location.length > 0
              ? response.data.location
              : LOC_DEFAULT
          );
        } finally {
          setLoading(false);
        }
      };

      fetchDetails();
    }
  }, [campaignId]);

  useEffect(() => {
    setIsAllSelected(
      selectedLocation.length === 0 ||
        (selectedLocation.length > 0 && selectedLocation[0]?.region === '')
    );
  }, [selectedLocation]);

  const handleAllChange = (e: { target: { checked: any } }) => {
    const checked = e.target.checked;
    setIsAllSelected(checked);
    setSelectedLocation(LOC_DEFAULT); // Clear other selections when "All" is checked
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    level: string,
    region = '',
    district = ''
  ) => {
    setIsAllSelected(false);
    const { value, checked } = e.target;
    setSelectedLocation((prev) => {
      let updatedLocation = [...prev];

      if (level === 'region') {
        // Update the region
        updatedLocation = [
          {
            region: value,
            district: [], // Reset district & suburb when selecting a new region
          },
        ];
      } else if (level === 'district') {
        updatedLocation[0].district = [
          {
            name: value,
            suburb: [], // Reset suburbs when selecting a new district
          },
        ];
      } else if (level === 'suburb') {
        const districtIndex = updatedLocation[0].district.findIndex(
          (d) => d.name === district
        );

        if (districtIndex !== -1) {
          const updatedDistrict: any = {
            ...updatedLocation[0].district[districtIndex],
          };

          if (checked) {
            updatedDistrict.suburb = [...updatedDistrict.suburb, value]; // Add suburb
          } else {
            updatedDistrict.suburb = updatedDistrict.suburb.filter(
              (s: any) => s !== value
            ); // Remove suburb
          }
          updatedLocation[0].district[districtIndex] = updatedDistrict;
        }
      }

      return updatedLocation;
    });
  };

  const getInitialValues = () => {
    const getSchedDateData = () => {
      if (!campaignData?.campaign_time) return null;
      const date = new Date(campaignData.campaign_time);

      return (
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0') +
        'T' +
        String(date.getHours()).padStart(2, '0') +
        ':' +
        String(date.getMinutes()).padStart(2, '0')
      );
    };

    const getMultiSelectValue = (val: any, opts: any[]) => {
      if (!val) return [];

      return opts.filter((o) => val.includes(o.value));
    };

    return {
      user_type: options.userType.find(
        (o) => campaignData?.user_type === o.value
      ) || {
        value: 'all',
        label: 'All',
      },
      buyerStatus: getMultiSelectValue(
        campaignData?.property_status,
        options.buyerSearchStatus
      ),
      sellerStatus: getMultiSelectValue(
        campaignData?.property_status,
        options.sellerSearchStatus
      ),
      finance_status: getMultiSelectValue(
        campaignData?.finance_status,
        options.buyerStatus
      ),
      property_type: getMultiSelectValue(
        campaignData?.property_type,
        options.propertyTypes
      ),
      market_price: getMultiSelectValue(
        campaignData?.market_price,
        campaignData?.user_type === 'sell'
          ? options.sellerMarketPrice
          : options.buyerMarketPrice
      ),
      broker_status: getMultiSelectValue(
        campaignData?.broker_status,
        Object.entries(options.facilitatorStatus).map(([key, value]) => ({
          value: value,
          label: ucFirstMultiple(key.replace(/_/g, ' ').toLowerCase()),
        }))
      ),
      campaign_name: campaignData?.campaign_name || '',
      email_subject: campaignData?.email_subject || '',
      email_body: campaignData?.email_body || 'Dear User,',
      email_footer:
        campaignData?.email_footer ||
        'Kind Regards, <br /><b>The Yelsa Team</b>',
      schedule: campaignData?.schedule || 'sendNow',
      scheduleDate: getSchedDateData(),
      registrationType: campaignData?.registrationType || '',
      date_from: campaignData?.dateFrom
        ? new Date(campaignData?.dateFrom).toISOString().split('T')[0]
        : null,
      date_to: campaignData?.dateTo
        ? new Date(campaignData?.dateTo).toISOString().split('T')[0]
        : null,
    };
  };

  if ((loading && campaignId) || options.userType.length === 0)
    return <Loader />;

  return (
    <>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={SCHEMA}
        onSubmit={(values) => {
          mutate(values);
        }}
      >
        {(formikProps: FormikProps<FormikValues>) => {
          const { values, errors, touched, handleChange, setFieldValue } =
            formikProps;

          return (
            <>
              <Form>
                <Flex flexDirection={'column'}>
                  <Flex flex={1} mb="1rem">
                    <Typography variant="type5" weight="700" color="primary">
                      {campaignId ? 'Update Template' : 'Create Template'}
                    </Typography>
                    <Flex flex={1} justifyContent="flex-end">
                      <Button variant="small" onClick={() => history.goBack()}>
                        Back
                      </Button>
                    </Flex>
                  </Flex>
                  <Box p={5} borderRadius="8px" boxShadow="lg" bg="white">
                    <HStack spacing={5} mb={'1rem'}>
                      <FormControl width="50%" isDisabled={!!campaignId}>
                        <FormLabel>User Type</FormLabel>
                        <StyledSelect
                          name="user_type"
                          value={values.user_type}
                          onChange={(val: any) =>
                            setFieldValue('user_type', val)
                          }
                          options={options.userType}
                          isDisabled={!!campaignId}
                        />
                      </FormControl>

                      {values.user_type.value === 'broker' && (
                        <>
                          <FormControl mb={'1rem'} width="50%">
                            <FormLabel>Professional Status</FormLabel>
                            <StyledSelect
                              name="broker_status"
                              value={values.broker_status}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('broker_status', val)
                              }
                              options={Object.entries(
                                options.facilitatorStatus
                              ).map(([key, value]) => ({
                                value: value,
                                label: ucFirstMultiple(
                                  key.replace(/_/g, ' ').toLowerCase()
                                ),
                              }))}
                            />
                          </FormControl>
                        </>
                      )}

                      {(values.user_type.value === 'buy' ||
                        values.user_type.value === 'sell') && (
                        <FormControl width="50%">
                          <FormLabel>Location</FormLabel>

                          <Menu>
                            <MenuButton
                              as={Button}
                              width="100%"
                              height="38px"
                              border="1px solid rgb(204, 204, 204)"
                            >
                              Select Location
                            </MenuButton>
                            <MenuList
                              width="170%"
                              maxHeight="50vh"
                              overflowY="auto"
                            >
                              <Box p={2}>
                                {/* "All" Option (Selected by Default) */}
                                <Checkbox
                                  value="all"
                                  isChecked={isAllSelected}
                                  onChange={handleAllChange}
                                >
                                  All
                                </Checkbox>
                              </Box>
                              {options.location.map((regionItem, idx) => (
                                <Box key={idx} p={2}>
                                  {/* Region Level */}
                                  <Checkbox
                                    value={regionItem.region}
                                    isChecked={selectedLocation.some(
                                      (loc) => loc.region === regionItem.region
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        e,
                                        'region',
                                        regionItem.region,
                                        ''
                                      )
                                    }
                                  >
                                    {regionItem.region}
                                  </Checkbox>

                                  {/* Show Districts & Suburbs only if user_type !== 'broker' */}
                                  {selectedLocation.some(
                                    (loc) => loc.region === regionItem.region
                                  ) && (
                                    <Box pl={4}>
                                      {regionItem.district.map(
                                        (
                                          districtItem: any,
                                          districtIdx: any
                                        ) => (
                                          <Box key={districtIdx} p={2}>
                                            {/* District Level */}
                                            <Checkbox
                                              value={districtItem.name}
                                              isChecked={selectedLocation.some(
                                                (loc) =>
                                                  loc.region ===
                                                    regionItem.region &&
                                                  loc.district.some(
                                                    (d) =>
                                                      d.name ===
                                                      districtItem.name
                                                  )
                                              )}
                                              onChange={(e) =>
                                                handleCheckboxChange(
                                                  e,
                                                  'district',
                                                  regionItem.region,
                                                  districtItem.name
                                                )
                                              }
                                            >
                                              {districtItem.name}
                                            </Checkbox>

                                            {selectedLocation.some(
                                              (loc) =>
                                                loc.region ===
                                                  regionItem.region &&
                                                loc.district.some(
                                                  (d) =>
                                                    d.name === districtItem.name
                                                )
                                            ) && (
                                              <Box pl={4}>
                                                {districtItem.suburb.map(
                                                  (
                                                    suburb: any,
                                                    suburbIdx: any
                                                  ) => (
                                                    <Box key={suburbIdx} p={2}>
                                                      {/* Suburb Level */}
                                                      <Checkbox
                                                        value={suburb}
                                                        isChecked={selectedLocation.some(
                                                          (loc) =>
                                                            loc.region ===
                                                              regionItem.region &&
                                                            loc.district.some(
                                                              (d) =>
                                                                d.name ===
                                                                  districtItem.name &&
                                                                d.suburb.includes(
                                                                  //@ts-ignore
                                                                  suburb
                                                                )
                                                            )
                                                        )}
                                                        onChange={(e) =>
                                                          handleCheckboxChange(
                                                            e,
                                                            'suburb',
                                                            regionItem.region,
                                                            districtItem.name
                                                          )
                                                        }
                                                      >
                                                        {suburb}
                                                      </Checkbox>
                                                    </Box>
                                                  )
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        )
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </MenuList>
                          </Menu>
                        </FormControl>
                      )}
                    </HStack>

                    {/* Display relevant fields based on the selected user_type */}
                    {values.user_type.value === 'buy' && (
                      <>
                        <HStack spacing={5} mb={'1rem'}>
                          <FormControl width={'50%'}>
                            <FormLabel>Buyer Status</FormLabel>
                            <StyledSelect
                              name="buyerStatus"
                              value={values.buyerStatus}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('buyerStatus', val)
                              }
                              options={options.buyerSearchStatus}
                            />
                          </FormControl>

                          <FormControl width={'50%'}>
                            <FormLabel>Finance Status</FormLabel>
                            <StyledSelect
                              name="finance_status"
                              value={values.finance_status}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('finance_status', val)
                              }
                              options={options.buyerStatus}
                            />
                          </FormControl>
                        </HStack>

                        <HStack spacing={5} mb={'1rem'}>
                          <FormControl>
                            <FormLabel>Property Type</FormLabel>
                            <StyledSelect
                              name="property_type"
                              value={values.property_type}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('property_type', val)
                              }
                              options={options.propertyTypes}
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Buyer Market Price</FormLabel>
                            <StyledSelect
                              name="market_price"
                              value={values.market_price}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('market_price', val)
                              }
                              options={options.buyerMarketPrice}
                            />
                          </FormControl>
                        </HStack>
                      </>
                    )}

                    {values.user_type.value === 'sell' && (
                      <>
                        <HStack spacing={5} mb={'1rem'}>
                          <FormControl>
                            <FormLabel>Seller Status</FormLabel>
                            <StyledSelect
                              name="sellerStatus"
                              value={values.sellerStatus}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('sellerStatus', val)
                              }
                              options={options.sellerSearchStatus}
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Property Type</FormLabel>
                            <StyledSelect
                              name="property_type"
                              value={values.property_type}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('property_type', val)
                              }
                              options={options.propertyTypes}
                            />
                          </FormControl>
                        </HStack>

                        <HStack mb={'1rem'}>
                          <FormControl>
                            <FormLabel>Seller Market Price</FormLabel>
                            <StyledSelect
                              name="market_price"
                              value={values.market_price}
                              placeholder={'All'}
                              isMulti
                              onChange={(val: any) =>
                                setFieldValue('market_price', val)
                              }
                              options={options.sellerMarketPrice}
                            />
                          </FormControl>
                        </HStack>
                      </>
                    )}

                    <VStack align="start" spacing={4} mb={'1rem'}>
                      <FormControl>
                        <FormLabel>
                          {values.user_type.value === 'broker' && 'Agent'}
                          {values.user_type.value === 'buy' && 'Buyer'}
                          {values.user_type.value === 'sell' && 'Property'}{' '}
                          Registration Date
                        </FormLabel>
                        <RadioGroup
                          name="registrationType"
                          value={values.registrationType}
                          onChange={(value) =>
                            setFieldValue('registrationType', value)
                          }
                        >
                          <HStack spacing={5}>
                            <Radio value="">All</Radio>
                            <Radio value="between">Registered Between</Radio>
                            <Radio value="by">Registered By</Radio>
                          </HStack>
                        </RadioGroup>
                      </FormControl>

                      {values.registrationType === 'between' && (
                        <HStack spacing={5} width="100%">
                          <FormControl width={'50%'}>
                            <FormLabel>Registered From</FormLabel>
                            <Input
                              type="date"
                              name="date_from"
                              value={values.date_from}
                              onChange={handleChange}
                            />
                            <StyledErrorMessage name="date_from" />
                          </FormControl>

                          <FormControl width={'50%'}>
                            <FormLabel>Registered To</FormLabel>
                            <Input
                              type="date"
                              name="date_to"
                              value={values.date_to}
                              onChange={handleChange}
                            />
                            <StyledErrorMessage name="date_to" />
                          </FormControl>
                        </HStack>
                      )}

                      {values.registrationType === 'by' && (
                        <FormControl width={'50%'}>
                          <FormLabel>Registered By</FormLabel>
                          <Input
                            type="date"
                            name="date_from"
                            value={values.date_from}
                            onChange={handleChange}
                          />
                          <StyledErrorMessage name="date_from" />
                        </FormControl>
                      )}
                    </VStack>

                    <FormControl
                      mb={'1rem'}
                      isInvalid={Boolean(
                        errors.campaign_name && touched.campaign_name
                      )}
                    >
                      <FormLabel>Campaign Name </FormLabel>
                      <Input
                        name="campaign_name"
                        value={values.campaign_name}
                        onChange={handleChange}
                      />
                      <StyledErrorMessage name="campaign_name" />
                    </FormControl>

                    <FormControl
                      mb={'1rem'}
                      isInvalid={Boolean(
                        errors.email_subject && touched.email_subject
                      )}
                    >
                      <FormLabel>Email Subject</FormLabel>
                      <Input
                        name="email_subject"
                        value={values.email_subject}
                        onChange={handleChange}
                      />
                      <StyledErrorMessage name="email_subject" />
                    </FormControl>

                    {/* Email Body (Rich Text Editor) */}
                    <FormControl mb={'1rem'}>
                      <FormLabel>Email Body</FormLabel>
                      <QuillEditor
                        value={values.email_body}
                        onChange={(value: any) =>
                          setFieldValue('email_body', value)
                        }
                      />
                    </FormControl>

                    {/* Email Body (Rich Text Editor) */}
                    <FormControl mb={'1rem'}>
                      <FormLabel>Email Footer</FormLabel>
                      <QuillEditor
                        value={values.email_footer}
                        onChange={(value: any) =>
                          setFieldValue('email_footer', value)
                        }
                      />
                    </FormControl>

                    {/* Send Now / Schedule Radio Buttons */}
                    <HStack spacing={5}>
                      <FormControl width={'50%'} isDisabled={!!campaignId}>
                        <FormLabel>Schedule</FormLabel>
                        <RadioGroup
                          name="schedule"
                          value={values.schedule}
                          onChange={(value) => setFieldValue('schedule', value)} // Corrected here
                        >
                          <HStack spacing={5}>
                            <Radio value="schedule">Yes</Radio>
                            <Radio value="sendNow">No</Radio>
                          </HStack>
                        </RadioGroup>
                      </FormControl>

                      {values.schedule === 'schedule' && (
                        <FormControl
                          isInvalid={Boolean(
                            errors.scheduleDate && touched.scheduleDate
                          )}
                          width={'50%'}
                        >
                          <FormLabel>Schedule Date</FormLabel>
                          <Input
                            type="datetime-local"
                            name="scheduleDate"
                            value={values.scheduleDate || ''}
                            onChange={handleChange}
                            min={new Date().toISOString().slice(0, -8)}
                          />
                          <StyledErrorMessage name="scheduleDate" />
                        </FormControl>
                      )}
                    </HStack>

                    <HStack display={'flex'} justifyContent={'end'} mt={'1rem'}>
                      <ButtonPrimitive
                        kind="secondary"
                        onClick={() => setIsPreviewOpen(true)}
                      >
                        Preview
                      </ButtonPrimitive>
                      <ButtonPrimitive
                        disabled={campaignData?.triggered}
                        type="submit"
                        onClick={() => {
                          setIsSavingTemplate(true);
                        }}
                      >
                        Save Template
                      </ButtonPrimitive>
                      <ButtonPrimitive
                        disabled={campaignData?.triggered}
                        loading={isLoading}
                        type="submit"
                      >
                        {values.schedule === 'schedule'
                          ? 'Send Later'
                          : 'Send Now'}
                      </ButtonPrimitive>
                    </HStack>
                  </Box>
                </Flex>
              </Form>

              <PreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                subject={values.email_subject}
                body={values.email_body}
                footer={values.email_footer}
              />
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default EmailTemplate;
